import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, Search, Filter, MapPin, X } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/shared/StatusBadge";
import api from "@/utils/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { TrackingTimeline } from "@/pages/public/TrackShipment";

const STATUS_OPTIONS = ["all", "booked", "picked_up", "in_transit", "out_for_delivery", "delivered", "cancelled"];

export default function MyShipments() {
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [trackOpen, setTrackOpen] = useState(null); // {awb, data, loading}

  const openTrack = async (awb, shipmentId) => {
    setTrackOpen({ awb, shipmentId, data: null, loading: true });
    try {
      const r = await api.get(`/v2/tracking/${awb}`);
      setTrackOpen({ awb, shipmentId, data: r.data, loading: false });
    } catch {
      setTrackOpen({ awb, shipmentId, data: null, loading: false, error: "Failed to load" });
    }
  };
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchShipments();
  }, [status]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (search) params.search = search;
      const res = await api.get("/shipments", { params });
      setShipments(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchShipments();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">My Shipments</h1>
            <p className="text-slate-500 text-sm mt-0.5">{total} total shipment{total !== 1 ? "s" : ""}</p>
          </div>
          <Link
            to="/customer/book"
            data-testid="new-shipment-btn"
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 uppercase"
          >
            <Package className="w-4 h-4" /> New Booking
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by tracking ID, city..."
                data-testid="shipments-search"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500"
              />
            </div>
            <button type="submit" className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600">Search</button>
          </form>
          <div className="flex gap-2 flex-wrap">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                data-testid={`filter-${s}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full capitalize transition-colors ${
                  status === s ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {s === "all" ? "All" : s.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading shipments...</div>
          ) : shipments.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No shipments found.</p>
              <Link to="/customer/book" className="text-brand-500 text-sm font-semibold hover:underline mt-2 inline-block">Book a shipment →</Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {["Tracking ID", "From", "To", "Weight", "Price", "Status", "Date", "Action"].map((h) => (
                      <th key={h} className="px-4 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {shipments.map((s, i) => (
                    <motion.tr
                      key={s.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      data-testid="shipment-table-row"
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <span className="font-heading font-bold text-brand-500 text-sm">{s.tracking_id}</span>
                          {s.eway_bill_required && !s.eway_bill_verified && (
                            <span title="Eway Bill required" className="text-amber-600" data-testid={`my-eway-warn-${s.tracking_id}`}>⚠️</span>
                          )}
                          {s.eway_bill_verified && (
                            <span title="Eway Bill verified" className="text-emerald-600 font-bold" data-testid={`my-eway-ok-${s.tracking_id}`}>✓</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.origin?.city}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.destination?.city}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.package?.weight} kg</td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-700">₹{s.price}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                      <td className="px-4 py-3 text-xs text-slate-400">{new Date(s.created_at).toLocaleDateString("en-IN")}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openTrack(s.tracking_id, s.id)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 px-2 py-1 rounded transition"
                          data-testid={`track-btn-${s.tracking_id}`}
                        >
                          <MapPin className="w-3.5 h-3.5" /> Track
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {trackOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={() => setTrackOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-xl shadow-2xl max-h-[85vh] overflow-auto" data-testid="track-modal">
            <div className="flex justify-between items-start p-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">Track Shipment — {trackOpen.awb}</h3>
              <button onClick={() => setTrackOpen(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {trackOpen.loading ? (
                <div className="py-10 text-center text-slate-400">Loading…</div>
              ) : trackOpen.data ? (
                <>
                  {trackOpen.shipmentId && (
                    <CustomerEwayBillCapture shipmentId={trackOpen.shipmentId} />
                  )}
                  <div className="text-sm text-slate-600 mb-4">
                    LR: <span className="font-semibold">{trackOpen.data.lr_number || "—"}</span>
                    {" · "}Current: <span className="font-semibold">{trackOpen.data.current_status || "—"}</span>
                  </div>
                  <TrackingTimeline events={trackOpen.data.events} embedded />
                </>
              ) : (
                <div className="py-10 text-center text-slate-400">No tracking events found</div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function CustomerEwayBillCapture({ shipmentId }) {
  const [info, setInfo] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/v2/shipments/${shipmentId}/eway-bill`);
        if (!cancel) setInfo(r.data);
      } catch { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [shipmentId]);

  if (!info || !info.eway_bill_required || info.eway_bill_verified) return null;

  const save = async () => {
    if (!/^\d{12}$/.test((val || "").trim())) {
      toast.error("Eway Bill must be exactly 12 digits");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/v2/shipments/${shipmentId}/eway-bill`, { eway_bill_number: val.trim() });
      toast.success("Eway Bill verified ✓");
      const r = await api.get(`/v2/shipments/${shipmentId}/eway-bill`);
      setInfo(r.data);
      setVal("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="customer-eway-banner">
      <div className="flex items-start gap-2">
        <span className="text-amber-600 text-lg">⚠️</span>
        <div className="flex-1 text-xs text-amber-800">
          <strong>Action required:</strong> Add Eway Bill number to enable pickup scheduling. Declared value exceeds ₹50,000.
        </div>
      </div>
      <div className="flex gap-2">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value.replace(/\D/g, "").slice(0, 12))}
          placeholder="12-digit Eway Bill"
          data-testid="customer-eway-input"
          className="flex-1 text-sm border border-amber-300 rounded px-2 py-1.5 focus:outline-none focus:border-amber-500"
        />
        <button
          onClick={save}
          disabled={busy || val.length !== 12}
          data-testid="customer-eway-save"
          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </div>
  );
}

